import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  EntityMentionType,
  type MentionableRecordDto,
} from '@paladin-monorepo/common';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { llmChatApi } from '@/lib/api/llm-chat';
import {
  parseMentionQuery,
  shouldFetchRecords,
  type MentionPrefixes,
} from '@/lib/llm-chat-mentions';
import { cn } from '@/lib/utils';

interface LlmChatMentionPickerProps {
  /** Text typed after the `@`. Empty means the prefix stage. */
  query: string;
  onSelectRecord: (record: MentionableRecordDto) => void;
  onSelectType: (type: EntityMentionType) => void;
  onClose: () => void;
  /** Raised so the composer can route Up/Down/Enter into the list. */
  registerKeyHandler: (handler: ((event: KeyboardEvent) => boolean) | null) => void;
}

const SEARCH_DEBOUNCE_MS = 150;

export function LlmChatMentionPicker({
  query,
  onSelectRecord,
  onSelectType,
  onClose,
  registerKeyHandler,
}: LlmChatMentionPickerProps) {
  const { t } = useTranslation();
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [activeIndex, setActiveIndex] = useState(0);

  const prefixes = useMemo<MentionPrefixes>(
    () => ({
      [EntityMentionType.Inquiry]: t('llmChat.mentions.types.inquiry'),
      [EntityMentionType.Offer]: t('llmChat.mentions.types.offer'),
      [EntityMentionType.Project]: t('llmChat.mentions.types.project'),
      [EntityMentionType.DecisionCase]: t('llmChat.mentions.types.decisionCase'),
      [EntityMentionType.CaseStudy]: t('llmChat.mentions.types.caseStudy'),
    }),
    [t],
  );

  // Which types the account holds at all. Changes rarely, so it is cached for the session.
  const typesQuery = useQuery({
    queryKey: ['llm-chat-mention-types'],
    queryFn: () => llmChatApi.getMentionTypes(),
    staleTime: 60_000,
  });
  const availableTypes = useMemo(() => typesQuery.data ?? [], [typesQuery.data]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  const parsed = useMemo(
    () => parseMentionQuery(debouncedQuery, prefixes, availableTypes),
    [debouncedQuery, prefixes, availableTypes],
  );

  // Records wait for a completed type prefix. Once it is in, they load with no name typed -
  // an empty `nameQuery` means "the most recently updated of that type".
  const wantsRecords = shouldFetchRecords(parsed);
  const recordsQuery = useQuery({
    queryKey: ['llm-chat-mention-search', parsed.nameQuery, parsed.type ?? null],
    queryFn: () => llmChatApi.searchMentions({ q: parsed.nameQuery, type: parsed.type }),
    enabled: wantsRecords,
    staleTime: 15_000,
  });

  const records = useMemo(
    () => (wantsRecords ? recordsQuery.data?.items ?? [] : []),
    [recordsQuery.data, wantsRecords],
  );
  // Once a type is chosen the prefix rows have served their purpose.
  const typeRows = useMemo(
    () => (wantsRecords ? [] : parsed.typeMatches),
    [parsed.typeMatches, wantsRecords],
  );
  const rowCount = typeRows.length + records.length;

  const resetKey = `${debouncedQuery}|${rowCount}`;
  const [lastResetKey, setLastResetKey] = useState(resetKey);
  if (lastResetKey !== resetKey) {
    setLastResetKey(resetKey);
    setActiveIndex(0);
  }

  useEffect(() => {
    const handler = (event: KeyboardEvent): boolean => {
      if (event.key === 'Escape') {
        onClose();
        return true;
      }
      if (rowCount === 0) return false;

      if (event.key === 'ArrowDown') {
        setActiveIndex((index) => (index + 1) % rowCount);
        return true;
      }
      if (event.key === 'ArrowUp') {
        setActiveIndex((index) => (index - 1 + rowCount) % rowCount);
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        if (activeIndex < typeRows.length) {
          onSelectType(typeRows[activeIndex]);
        } else {
          onSelectRecord(records[activeIndex - typeRows.length]);
        }
        return true;
      }
      return false;
    };

    registerKeyHandler(handler);
    return () => registerKeyHandler(null);
  }, [activeIndex, onClose, onSelectRecord, onSelectType, records, registerKeyHandler, rowCount, typeRows]);

  const noRecordTypes = !typesQuery.isLoading && availableTypes.length === 0;
  const failed = typesQuery.isError || recordsQuery.isError;

  return (
    <div
      className="absolute bottom-full left-0 z-50 mb-2 w-full max-w-md overflow-hidden rounded-lg border bg-popover shadow-md"
      // Keeps focus in the composer while a row is clicked. Without this the textarea blurs
      // on mousedown, the picker unmounts, and the click never reaches the row - the popup
      // just closes and nothing is inserted.
      onMouseDown={(event) => event.preventDefault()}
    >
      <Command shouldFilter={false} className="max-h-72">
        <CommandList>
          {failed && (
            <div className="px-3 py-2 text-sm text-destructive">{t('llmChat.mentions.loadFailed')}</div>
          )}

          {!failed && noRecordTypes && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {t('llmChat.mentions.noRecords')}
            </div>
          )}

          {!failed && !noRecordTypes && typeRows.length > 0 && (
            <CommandGroup>
              {typeRows.map((type, index) => (
                <CommandItem
                  key={type}
                  value={`type-${type}`}
                  onSelect={() => onSelectType(type)}
                  className={cn('cursor-pointer', index === activeIndex && 'bg-accent')}
                >
                  <span className="font-medium">{prefixes[type]}:</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {!failed && wantsRecords && recordsQuery.isPending && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {t('llmChat.mentions.loading')}
            </div>
          )}

          {!failed && !noRecordTypes && !wantsRecords && typeRows.length > 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              {t('llmChat.mentions.pickTypeHint')}
            </div>
          )}

          {!failed && records.length > 0 && (
            <CommandGroup>
              {records.map((record, index) => {
                const rowIndex = typeRows.length + index;
                return (
                  <CommandItem
                    key={`${record.type}:${record.id}`}
                    value={`record-${record.type}-${record.id}`}
                    onSelect={() => onSelectRecord(record)}
                    className={cn(
                      'flex cursor-pointer flex-col items-start gap-0.5',
                      rowIndex === activeIndex && 'bg-accent',
                    )}
                  >
                    <span className="truncate">
                      <span className="text-muted-foreground">{prefixes[record.type]}: </span>
                      {record.name || t('llmChat.mentions.untitled')}
                    </span>
                    {record.detail && (
                      <span className="text-xs text-muted-foreground">{record.detail}</span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          {!failed && !noRecordTypes && !recordsQuery.isPending && !recordsQuery.isFetching && rowCount === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {t('llmChat.mentions.noMatches')}
            </div>
          )}

          {!failed && recordsQuery.data?.hasMore && (
            <div className="border-t px-3 py-2 text-xs text-muted-foreground">
              {t('llmChat.mentions.keepTyping')}
            </div>
          )}
        </CommandList>
      </Command>
    </div>
  );
}
